import { useSuspenseQuery } from '@apollo/client';
import { CollectionType } from '@/__generated__/graphql';
import StandardAutocompleteIdsWithFallback from '@features/standardDesign/components/autocomplete/StandardAutocompleteIds';
import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, TextField } from '@mui/material';
import Fuse from 'fuse.js';
import { useEffect, useState, type FC } from 'react';
import { FixedSizeList } from 'react-window-1.8.10';
import GET_COLLECTIONS_FOR_DEVICE_ACCESS, {
  AssignedCollectionForDeviceAccess,
  CollectionForDeviceAccess,
} from '../graphql/queries/getCollectionsForDeviceAccess';
import { CollectionDeviceValue } from '../types';
import CollectionsListItem from './CollectionsListItem';

const TYPE_ORDER: Record<CollectionType, number> = {
  [CollectionType.Organization]: 0,
  [CollectionType.Icn]: 1,
  [CollectionType.Custom]: 2,
  [CollectionType.Dga]: 3,
};

const sortByTypeThenName = (collections: CollectionForDeviceAccess[]) =>
  [...collections].sort((a, b) => {
    const typeDiff = TYPE_ORDER[a.type] - TYPE_ORDER[b.type];
    if (typeDiff !== 0) return typeDiff;
    return a.name.localeCompare(b.name);
  });

interface DeviceCollectionsAccessInputProps {
  deviceId: string;
  organizationIds: number[];
  onOrganizationIdsChange: (ids: number[]) => void;
  value: CollectionDeviceValue[];
  onChange: (values: CollectionDeviceValue[]) => void;
  /** @default 450 */
  height?: number;
}

const DeviceCollectionsAccessInput: FC<DeviceCollectionsAccessInputProps> = ({
  deviceId,
  organizationIds,
  onOrganizationIdsChange,
  value,
  onChange,
  height,
}) => {
  const { _ } = useLingui();
  const { data } = useSuspenseQuery(GET_COLLECTIONS_FOR_DEVICE_ACCESS, {
    variables: {
      organizationIds,
      deviceId,
    },
  });
  const [search, setSearch] = useState('');

  const assignedByCollectionId = new Map(
    data.assignedCollections.map((relation) => [relation.id, relation.deviceCollectionRelationId]),
  );

  // AMARU-609 (mirror de CollectionsAccessForUserInput / AMARU-562): una colección ya asignada
  // al dispositivo pero fuera del filtro de organización actual debe seguir visible — se agrega
  // como entry que no está en `allCollections`.
  const allCollectionIds = new Set(data.allCollections.map((c) => c.id));
  const extraAssignedCollections: AssignedCollectionForDeviceAccess[] = data.assignedCollections.filter(
    (c) => !allCollectionIds.has(c.id),
  );
  const lockedCollectionIds = new Set(
    data.assignedCollections.filter((c) => !c.callerCanManageAccess).map((c) => c.id),
  );
  const mergedCollections = [...data.allCollections, ...extraAssignedCollections];

  const [filteredCollections, setFilteredCollections] = useState(() =>
    sortByTypeThenName(mergedCollections),
  );

  useEffect(() => {
    let filteredItems = mergedCollections;

    if (search) {
      const fuse = new Fuse(filteredItems, { keys: ['name'] });
      filteredItems = fuse.search(search).map(({ item }) => item);
    }

    setFilteredCollections(sortByTypeThenName(filteredItems));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, data.allCollections, data.assignedCollections]);

  const onSearchChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    setSearch(event.target.value);
  };

  const getHasAccess = (collection: CollectionForDeviceAccess) => {
    const formValue = value.find((item) => item.collectionId === collection.id);
    if (formValue) return formValue.hasAccess;
    return assignedByCollectionId.has(collection.id);
  };

  return (
    <>
      <Box mb={1} display="flex" justifyContent="stretch" alignItems="center" gap={2}>
        <TextField
          value={search}
          onChange={onSearchChange}
          size="small"
          label={_(msg`Buscar`)}
          sx={{ flex: 3 }}
        />

        <StandardAutocompleteIdsWithFallback
          sx={{ flex: 2 }}
          size="small"
          dataType="organizations"
          multiple
          multipleCheckboxes
          value={organizationIds}
          onChange={(_, values) => onOrganizationIdsChange(values)}
        />
      </Box>
      <Box border={1} borderColor="divider" borderRadius={1}>
        <FixedSizeList
          height={height ?? 450}
          width="100%"
          itemSize={56}
          itemCount={filteredCollections.length}
          itemKey={(index: number) => filteredCollections[index].id}
        >
          {({ style, index }: { style: React.CSSProperties; index: number }) => {
            const collection = filteredCollections[index];
            const locked = lockedCollectionIds.has(collection.id);
            const formValue = value.find((item) => item.collectionId === collection.id);
            const hasAccess = getHasAccess(collection);
            const modified = formValue != null;
            const lastItem = index === filteredCollections.length - 1;
            const onClick = () => {
              if (locked) return;
              const newValues = value.filter((item) => item.collectionId !== collection.id);
              const wasAssigned = assignedByCollectionId.has(collection.id);
              const newValue = { collectionId: collection.id, hasAccess: !hasAccess };
              if (newValue.hasAccess === wasAssigned) onChange(newValues);
              else onChange([...newValues, newValue]);
            };

            return (
              <CollectionsListItem
                style={style}
                key={collection.id}
                collectionId={collection.id}
                collectionName={collection.name}
                collectionType={collection.type}
                organizationName={collection.organization?.name}
                onClick={onClick}
                hasAccess={hasAccess}
                modified={modified}
                lastItem={lastItem}
                locked={locked}
              />
            );
          }}
        </FixedSizeList>
      </Box>
    </>
  );
};

export default DeviceCollectionsAccessInput;
